'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import useFetchCoatingPricing from '../../hooks/useFetchCoatingPricing';

const PipeSpecsGrid = () => {
  const { formData } = useEstimator();
  const { coatingPricing } = useFetchCoatingPricing();

  const formatNegatives = (value, decimals = 3) => {
    if (value === '' || value === null || isNaN(value)) return '';
    return value < 0 ? `(${Math.abs(value).toFixed(decimals)})` : value.toFixed(decimals);
  };

  const [pipeSpecsData, setPipeSpecsData] = useState([
    { lineClass: 'Class 1', baseQty: 0, classLocation: 0.72 },
    { lineClass: 'Class 2', baseQty: formData.quantities[23].quantity, classLocation: 0.6 },
    { lineClass: 'Class 3', baseQty: formData.quantities[24].quantity, classLocation: 0.5 },
    { lineClass: 'Class 4', baseQty: formData.quantities[25].quantity, classLocation: 0.4 },
    { lineClass: 'Bore Pipe Class 1', baseQty: formData.quantities[13]?.quantity, classLocation: 0.6 },
    { lineClass: 'Bore Pipe Class 2', baseQty: formData.quantities[15]?.quantity, classLocation: 0.5 },
    { lineClass: 'Bore Pipe Class 3', baseQty: formData.quantities[17]?.quantity, classLocation: 0.4 },
    { lineClass: 'Direct Pipe', baseQty: formData.quantities[20]?.quantity, classLocation: 0.5 },
    { lineClass: 'HDD', baseQty: formData.quantities[19]?.quantity, classLocation: 0.5 },
  ]);

  const tensileStrengthMap = {
    'X-42': 42000,
    'X-46': 46000,
    'X-52': 52000,
    'X-56': 56000,
    'X-60': 60000,
    'X-65': 65000,
    'X-70': 70000,
    'X-80': 80000,
    'X-90': 90000,
  };

  useEffect(() => {
    const totalOtherClasses = [
      'Class 2',
      'Class 3',
      'Class 4',
      'Bore Pipe Class 1',
      'Bore Pipe Class 2',
      'Bore Pipe Class 3',
      'Direct Pipe',
      'HDD',
    ].reduce((sum, cls) => {
      const match = pipeSpecsData.find((row) => row.lineClass === cls);
      return sum + (parseFloat(match?.baseQty) || 0);
    }, 0);

    const class1BaseQty = Math.max(formData.quantities[5].quantity - totalOtherClasses, 0);

    console.log('🔍 Class 1 Qty Debug:', {
      originalQty: formData.quantities[5].quantity,
      totalOtherClasses,
      finalClass1BaseQty: class1BaseQty,
    });

    const updated = pipeSpecsData.map((row) => {
      let baseQty =
        row.lineClass === 'Class 1'
          ? class1BaseQty
          : parseFloat(row.baseQty) || 0;

      const classLocation = parseFloat(row.classLocation) || 0;
      const updatedTensileStrength = parseFloat(row.tensileStrength) || 0;
      const maop = parseFloat(formData.quantities[8].quantity) || 0;
      const size = parseFloat(formData.quantities[0].quantity) || 0;
      const overBuy = parseFloat(row.overBuy) || 0;
      let orderQty = parseFloat(row.orderQty) || 0;
      const wtOverride = parseFloat(row.wtOverride);

      if (
        [
          'Class 1',
          'Class 2',
          'Class 3',
          'Class 4',
          'Bore Pipe Class 1',
          'Bore Pipe Class 2',
          'Bore Pipe Class 3',
          'Direct Pipe',
          'HDD',
        ].includes(row.lineClass)
      ) {
        orderQty = Math.round(baseQty * (1 + overBuy / 100));
      }

      // --- Wall Thickness ---
      let wallThickness = '';
      if (baseQty && updatedTensileStrength && classLocation) {
        const rawWall = (maop * size) / (2 * updatedTensileStrength * classLocation);
        wallThickness = Math.ceil(rawWall * 1000) / 1000;
      }

      // --- Tons ---
      let tons = 0;
      const activeWall = isNaN(wtOverride) ? wallThickness : wtOverride;
      if (baseQty && activeWall && !isNaN(activeWall)) {
        tons = (10.69 * (size - activeWall) * activeWall * ((1 + overBuy / 100) * orderQty)) / 2000;
        tons = Math.round(tons * 1000) / 1000;
      }

      // --- lbs/ft ---
      let lbsPerFt = 0;
      if (baseQty && size) {
        lbsPerFt = isNaN(wtOverride)
          ? (size * wallThickness - wallThickness ** 2) * 10.69
          : (size * wtOverride - wtOverride ** 2) * 10.69;
        lbsPerFt = Math.round(lbsPerFt * 1000) / 1000;
      }

      // --- Internal Coating ---
      let coatingCostPerFt = 0;
      let coatingPrice = 0;

      if (row.internalCoating === 'Yes' && coatingPricing.length > 0 && size) {
        const matched = coatingPricing.find((item) => Number(item.pipeOD) === Number(size));

        const isBorePipe =
          row.lineClass === 'Bore Pipe Class 1' ||
          row.lineClass === 'Bore Pipe Class 2' ||
          row.lineClass === 'Bore Pipe Class 3' ||
          row.lineClass === 'Direct Pipe' ||
          row.lineClass === 'HDD';

        if (matched) {
          if (isBorePipe && matched.aroPricePerFoot != null) {
            coatingCostPerFt = parseFloat(matched.aroPricePerFoot);
            coatingPrice = coatingCostPerFt * orderQty;
          } else if (!isBorePipe && matched.fbePricePerFoot != null) {
            coatingCostPerFt = parseFloat(matched.fbePricePerFoot);
            coatingPrice = coatingCostPerFt * orderQty;
          }
        }
      }

      return {
        ...row,
        baseQty,
        orderQty,
        wallThickness,
        tons,
        lbsPerFt,
        coatingCostPerFt,
        coatingPrice,
      };
    });

    setPipeSpecsData(updated);
  }, [formData.quantities, coatingPricing]);

  const columnDefs = useMemo(
    () => [
      { headerName: 'Line Class', field: 'lineClass', width: 160, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' } },
      { headerName: 'Base Quantity (ft)', field: 'baseQty', cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, width: 180 },
      { headerName: '% Over Buy', field: 'overBuy', editable: true, width: 180 },
      { headerName: 'Order Quantity (ft)', field: 'orderQty', cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, width: 180 },
      {
        headerName: 'Grade',
        field: 'grade',
        editable: true,
        width: 180,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: Object.keys(tensileStrengthMap) },
      },
      { headerName: 'MAOP', field: 'maop', valueGetter: () => formData.quantities[8].quantity, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, width: 180 },
      { headerName: 'WT Override', field: 'wtOverride', editable: true, width: 180 },
      { headerName: 'Wall Thickness', field: 'wallThickness', cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, width: 180 },
      { headerName: 'Size', field: 'size', valueGetter: () => formData.quantities[0].quantity, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, width: 180 },
      {
        headerName: 'Tensile Strength',
        field: 'tensileStrength',
        editable: false,
        width: 180,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
        valueFormatter: ({ value }) => (value === '' || value === null || isNaN(Number(value)) ? '' : Number(value).toLocaleString()),
      },
      { headerName: 'Class Location', field: 'classLocation', cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, width: 180 },
      { headerName: 'Tons', field: 'tons', width: 180, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, valueFormatter: ({ value }) => formatNegatives(value) },
      { headerName: 'lbs/ft', field: 'lbsPerFt', width: 180, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' }, valueFormatter: ({ value }) => formatNegatives(value) },
      {
        headerName: 'Internal Coating',
        field: 'internalCoating',
        editable: true,
        width: 180,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ['Yes', 'No'] },
      },
      {
        headerName: 'Coating $/ft',
        field: 'coatingCostPerFt',
        width: 180,
        valueFormatter: ({ value }) => (value < 0 ? `(${Math.abs(value).toFixed(2)})` : value?.toFixed(2) || ''),
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      {
        headerName: 'Coating Price',
        field: 'coatingPrice',
        width: 180,
        valueFormatter: ({ value }) => (value < 0 ? `(${Math.abs(value).toFixed(2)})` : value?.toFixed(2) || ''),
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
    ],
    []
  );

  const handleGridEdit = (event) => {
    const updated = [...pipeSpecsData];
    const row = { ...event.data };

    const baseQty = parseFloat(row.baseQty) || 0;
    const overBuy = parseFloat(row.overBuy) || 0;
    const maop = parseFloat(formData.quantities[8].quantity) || 0;
    const size = parseFloat(formData.quantities[0].quantity) || 0;
    const classLocation = parseFloat(row.classLocation) || 0;
    const wtOverride = parseFloat(row.wtOverride);
    const orderQty = parseFloat(row.orderQty) || 0;

    // Recalculate Order Quantity for Class 2–4 and Bore Pipe Classes
    if (
      [
        'Class 1',
        'Class 2',
        'Class 3',
        'Class 4',
        'Bore Pipe Class 1',
        'Bore Pipe Class 2',
        'Bore Pipe Class 3',
        'Direct Pipe',
        'HDD',
      ].includes(row.lineClass)
    ) {
      row.orderQty = Math.round(baseQty * (1 + overBuy / 100));
    }

    // Set tensile strength based on grade
    if (row.grade && tensileStrengthMap[row.grade]) {
      row.tensileStrength = tensileStrengthMap[row.grade];
    }
    const updatedTensileStrength = parseFloat(row.tensileStrength) || 0;

    // Wall Thickness calculation using updated tensile strength
    if (baseQty === 0) {
      row.wallThickness = '';
    } else {
      try {
        const denominator = 2 * updatedTensileStrength * classLocation;
        if (denominator === 0) {
          row.wallThickness = 0;
        } else {
          const rawWall = (maop * size) / denominator;
          const roundedUpWall = Math.ceil(rawWall * 1000) / 1000;
          row.wallThickness = roundedUpWall;
        }
      } catch {
        row.wallThickness = 0;
      }
    }

    // Tons Calculation
    if (baseQty === 0) {
      row.tons = 0;
    } else {
      const activeWall = isNaN(wtOverride) ? row.wallThickness : wtOverride;

      // If wall thickness is 0, empty, or NaN, result is 0
      if (!activeWall || isNaN(activeWall)) {
        row.tons = 0;
      } else {
        try {
          const tons = (10.69 * (size - activeWall) * activeWall * ((1 + overBuy / 100) * orderQty)) / 2000;
          row.tons = Math.round(tons * 1000) / 1000;
        } catch {
          row.tons = 0;
        }
      }
    }

    // lbs/ft Calculation
    if (baseQty === 0) {
      row.lbsPerFt = 0;
    } else {
      try {
        const lbsPerFt = isNaN(wtOverride)
          ? (size * row.wallThickness - row.wallThickness ** 2) * 10.69
          : (size * wtOverride - wtOverride ** 2) * 10.69;

        row.lbsPerFt = Math.round(lbsPerFt * 1000) / 1000;
      } catch {
        row.lbsPerFt = 0;
      }
    }

    if (row.internalCoating === 'Yes' && coatingPricing.length > 0 && size) {
      const matched = coatingPricing.find((item) => Number(item.pipeOD) === Number(size));

      const isBorePipe =
        row.lineClass === 'Bore Pipe Class 1' ||
        row.lineClass === 'Bore Pipe Class 2' ||
        row.lineClass === 'Bore Pipe Class 3' ||
        row.lineClass === 'Direct Pipe' ||
        row.lineClass === 'HDD';

      if (matched) {
        if (isBorePipe && matched.aroPricePerFoot != null) {
          row.coatingCostPerFt = parseFloat(matched.aroPricePerFoot);
          row.coatingPrice = row.coatingCostPerFt * orderQty;
        } else if (!isBorePipe && matched.fbePricePerFoot != null) {
          row.coatingCostPerFt = parseFloat(matched.fbePricePerFoot);
          row.coatingPrice = row.coatingCostPerFt * orderQty;
        } else {
          row.coatingCostPerFt = 0;
          row.coatingPrice = 0;
        }
      } else {
        row.coatingCostPerFt = 0;
        row.coatingPrice = 0;
      }
    } else {
      row.coatingCostPerFt = 0;
      row.coatingPrice = 0;
    }

    updated[event.rowIndex] = row;
    setPipeSpecsData(updated);
  };

  return (
    <Box>
      <Typography
        variant="h6"
        mb={2}
        sx={{
          backgroundColor: '#0C1D3C',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        Pipe Specs
      </Typography>
      <Box sx={{ maxHeight: 400, overflowY: 'auto', width: '100%' }}>
        <AgGridReact
          rowData={pipeSpecsData}
          columnDefs={columnDefs}
          onCellValueChanged={handleGridEdit}
          defaultColDef={{ resizable: true, cellStyle: { textAlign: 'left' } }}
          suppressMovableColumns
          domLayout="autoHeight"
        />
      </Box>
    </Box>
  );
};

export default PipeSpecsGrid;
